import { openContactLinksDialog } from "@components/layouts/ContactLinksDialog";
import { STORAGE_KEY } from "@constants/misc";
import { safeParseJson } from "@utils/misc";
import { Banner, ContactUrl } from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import useServerInfo from "./useServerInfo";
import useTypedSelector from "./useTypedSelector";

interface UseContactLinksProps {
  preLoginOnly?: boolean;
  fallbackToLocalStorage?: boolean;
}

const useContactLinks = ({ preLoginOnly = false, fallbackToLocalStorage = false }: UseContactLinksProps = {}) => {
  const { contactBanners: preLoginContactBannersServer, groupedContactIcon: preLoginGroupedContactIconServer } =
    useServerInfo() ?? {};
  const { contactBanners: postLoginContactBannersServer, groupedContactIcon: postLoginGroupedContactIconServer } =
    useTypedSelector((state) => state.auth);

  // Get contact banners and grouped contact icon from server, fallback to local storage
  const { preLoginContactBanners, postLoginContactBanners, preLoginGroupedContactIcon, postLoginGroupedContactIcon } =
    useMemo(() => {
      const cachedPreLoginContactBanners = safeParseJson<Banner[]>(
        localStorage.getItem(STORAGE_KEY.PUBLIC_CONTACT_BANNERS) ?? "[]",
      );
      const cachedPostLoginContactBanners = safeParseJson<Banner[]>(
        localStorage.getItem(STORAGE_KEY.PRIVATE_CONTACT_BANNERS) ?? "[]",
      );
      const cachedPreLoginGroupedContactIcon = localStorage.getItem(STORAGE_KEY.PUBLIC_GROUPED_CONTACT_ICON);
      const cachedPostLoginGroupedContactIcon = localStorage.getItem(STORAGE_KEY.PRIVATE_GROUPED_CONTACT_ICON);

      const preLoginContactBanners =
        preLoginContactBannersServer || (fallbackToLocalStorage && cachedPreLoginContactBanners) || [];
      const postLoginContactBanners =
        postLoginContactBannersServer || (fallbackToLocalStorage && cachedPostLoginContactBanners) || [];

      const preLoginGroupedContactIcon =
        preLoginGroupedContactIconServer || (fallbackToLocalStorage && cachedPreLoginGroupedContactIcon) || undefined;

      const postLoginGroupedContactIcon =
        postLoginGroupedContactIconServer || (fallbackToLocalStorage && cachedPostLoginGroupedContactIcon) || undefined;

      return {
        preLoginContactBanners,
        postLoginContactBanners,
        preLoginGroupedContactIcon,
        postLoginGroupedContactIcon,
      };
    }, [
      preLoginContactBannersServer,
      postLoginContactBannersServer,
      preLoginGroupedContactIconServer,
      postLoginGroupedContactIconServer,
      fallbackToLocalStorage,
    ]);

  const contactLinks = useMemo(() => {
    const preLoginContactLinks: ContactUrl[] =
      preLoginContactBanners?.map(
        (item) =>
          ({
            id: item?.id,
            url: item?.action?.url,
            image: item?.primaryImage?.url,
            description: item?.name,
          }) as ContactUrl,
      ) ?? [];
    const cleansedPreLinks = preLoginContactLinks.filter((link) => link.url);

    if (preLoginOnly) {
      return cleansedPreLinks as ContactUrl[];
    }

    const postLoginContactLinks: ContactUrl[] =
      postLoginContactBanners?.map(
        (item) =>
          ({
            id: item?.id,
            url: item?.action?.url,
            image: item?.primaryImage?.url,
            description: item?.name,
          }) as ContactUrl,
      ) ?? [];

    const cleansedPostLinks = postLoginContactLinks.filter((link) => link.url);

    const renderedLinks = cleansedPostLinks.length ? cleansedPostLinks : cleansedPreLinks;

    return renderedLinks as ContactUrl[];
  }, [preLoginContactBanners, postLoginContactBanners, preLoginOnly]);

  const openContact = useCallback(() => {
    if (!contactLinks?.length) return;
    if (contactLinks.length === 1) {
      window.open(contactLinks[0].url, "_blank", "noopener,noreferrer");
    } else {
      openContactLinksDialog({
        contactLinks,
      });
    }
  }, [contactLinks]);

  const groupedContactIcon = useMemo(() => {
    return postLoginGroupedContactIcon || preLoginGroupedContactIcon;
  }, [postLoginGroupedContactIcon, preLoginGroupedContactIcon]);

  return { contactLinks, groupedContactIcon, openContact };
};

export default useContactLinks;
